/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button, useMantineTheme, rem, Text, Box, Flex } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { RiArrowRightLine, RiCloseLine } from '@remixicon/react';

import { TextVariants } from '@fai/ui/components/typography/variants';
import type { PropsWithChildren } from '@fai/util/types';

import type { HeaderMessageProps } from './model';

function CloseButton({ handleOnClick }: { handleOnClick?: VoidFunction }) {
  const theme = useMantineTheme();

  return (
    <Button
      variant="icon-transparent"
      c={theme.pf.color.fg.neutral.default}
      onClick={handleOnClick}
    >
      <RiCloseLine size={18} />
    </Button>
  );
}

function ContentActionButton({
  children,
  handleOnClick,
}: PropsWithChildren<{
  handleOnClick?: VoidFunction;
}>) {
  return (
    <Button
      theme="cloud"
      variant="transparent"
      size="sm"
      h={rem(21)}
      p={0}
      rightSection={<RiArrowRightLine size={16} />}
      onClick={handleOnClick}
    >
      {children}
    </Button>
  );
}

function Content({ children, color }: PropsWithChildren<{ color: string }>) {
  return (
    <Text c={color} lineClamp={2} ta="center" truncate="end" variant={TextVariants.BodyMd}>
      {children}
    </Text>
  );
}

/**
 * @structure ( content + content.action ) / closeButton
 * @compound `HeaderMessage.ContentActionButton` can be used for contentButton prop
 * @example
 * <HeaderMessage
 *   contentButton={
 *    <HeaderMessage.ContentActionButton
 *      handleOnClick={openIntercomMessageTab}
 *    >
 *      click!
 *    </HeaderMessage.ContentActionButton>
 *   }
 * >
 *  content
 * </HeaderMessage>
 */
function HeaderMessage({
  variant,
  isCloseable,
  contentButton,
  onClose,
  children,
}: PropsWithChildren<HeaderMessageProps>) {
  const theme = useMantineTheme();

  const [opened, { close }] = useDisclosure(true);

  const backgroundColor =
    variant === 'INFO'
      ? theme.pf.color.bg.cloudblue.default.base
      : theme.pf.color.bg.danger.default.base;
  const contentTextColor =
    variant === 'INFO' ? theme.pf.color.fg.cloudblue.strong : theme.pf.color.fg.danger.strong;

  const onCloseHeaderMessage = () => {
    close();

    if (onClose) onClose();
  };

  if (!opened) return null;

  return (
    <Flex
      align="center"
      bg={backgroundColor}
      direction={{ base: 'column', lg: 'row' }}
      justify={{ base: 'center', lg: 'space-between' }}
      mih={{ base: 'fit-content', lg: rem(56) }}
      px={{ base: theme.pf.layout.sm, lg: 0 }}
      py={{ base: theme.pf.layout.xs, lg: 0 }}
    >
      {/* Empty box for space-between */}
      <Box visibleFrom="lg" />

      <Flex direction={{ base: 'column', lg: 'row' }} gap={theme.pf.layout['3xs']}>
        <Content color={contentTextColor}>{children}</Content>
        {contentButton}
      </Flex>

      <Box visibleFrom="lg">
        {isCloseable && <CloseButton handleOnClick={onCloseHeaderMessage} />}
      </Box>
    </Flex>
  );
}

HeaderMessage.displayName = 'HeaderMessage';

HeaderMessage.ContentActionButton = ContentActionButton;

export default HeaderMessage;
